<template>
    <div class="image-uploader">
        <div class="options d-flex justify-content-center">
            <div class="btn-group" role="group" aria-label="Formato de imágenes">
                <button
type="button" class="btn"
                    :class="{ 'btn-primary': imagesPerRow === 1, 'btn-outline-primary': imagesPerRow !== 1 }"
                    @click="imagesPerRow = 1">1 Imagen por fila</button>
                <button
type="button" class="btn"
                    :class="{ 'btn-primary': imagesPerRow === 2, 'btn-outline-primary': imagesPerRow !== 2 }"
                    @click="imagesPerRow = 2">2 Imágenes por fila</button>
                <button
type="button" class="btn"
                    :class="{ 'btn-primary': imagesPerRow === 3, 'btn-outline-primary': imagesPerRow !== 3 }"
                    @click="imagesPerRow = 3">3 Imágenes por fila</button>
                <button
type="button" class="btn"
                    :class="{ 'btn-primary': imagesPerRow === 4, 'btn-outline-primary': imagesPerRow !== 4 }"
                    @click="imagesPerRow = 4">4 Imágenes por fila</button>
            </div>
        </div>

        <div class="grid" :style="gridStyle">
            <div
v-for="(image, index) in signedImages" :key="index" class="image-box" draggable="true"
                @dragstart="onDragStart(index)" @dragover.prevent @drop="onDrop(index)">
                <input type="file" accept="image/*" class="file-input" @change="onFileChange($event, index)" />
                <img v-if="image" :src="image" alt="Uploaded Image" @error="onImageError(index)" />
                <div class="buttons mt-auto">
                    <button
class="btn-primary file-button"
                        @click.prevent="triggerFileInput(index)">Seleccionar</button>
                    <button
v-if="image" class="btn-danger delete-button"
                        @click.prevent="deleteImage(index)">Eliminar</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import api from '@/services/api';

const props = defineProps({
    patientRecord: {
        type: Object,
        required: true
    },
    localNoteForm: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['update:imagesUrl']);

const images = ref(new Array(8).fill(null)); // Paths relativos (guardados en BD)
const signedImages = ref(new Array(8).fill(null)); // URLs firmadas (para mostrar)
const imagesPerRow = ref(2);
const dragIndex = ref(null);

const onFileChange = async (event, index) => {
    try {
        const file = event.target.files[0];
        if (file) {
            console.log('📁 Archivo seleccionado:', file.name);
            const result = await uploadToBackend(file);
            images.value[index] = result.path; // Guardar path relativo
            signedImages.value[index] = result.signedUrl; // Guardar URL firmada para mostrar
            await saveImagesToServer();
            emitUpdateImagesUrl();
            console.log('✅ Imagen guardada exitosamente');
        }
    } catch (error) {
        console.error("❌ Error en onFileChange:", error);
        alert(`Error al subir la imagen: ${error.message}\n\nRevisa la consola del navegador (F12) para más detalles.`);
    }
};

const uploadToBackend = async (file) => {
    try {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('patient_id', props.patientRecord.id);

        // document_id es opcional según la API
        if (props.localNoteForm.id) {
            formData.append('document_id', props.localNoteForm.id);
        }

        console.log('📤 Subiendo imagen de reporte al backend...', {
            patient_id: props.patientRecord.id,
            document_id: props.localNoteForm.id || 'sin asignar',
            fileName: file.name
        });

        const response = await api.post('/patient-documents/upload-image', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        console.log('📥 Respuesta del backend:', response.data);

        // Respuesta con bucket privado:
        // image_url: path relativo (guardar en BD)
        // signed_url: URL firmada temporal (mostrar en UI)
        const imageUrl = response.data?.data?.image_url; // Path relativo
        const signedUrl = response.data?.data?.signed_url; // URL firmada

        if (!imageUrl || !signedUrl) {
            console.error('❌ No se recibió image_url o signed_url en la respuesta:', response.data);
            throw new Error('No se recibió URL del servidor. Revisa la consola para ver la respuesta completa.');
        }

        console.log('✅ Imagen subida exitosamente:', {
            path: imageUrl, // Path relativo para BD
            signed_url: signedUrl, // URL firmada para UI
            expires_in: response.data.data.expires_in,
            mime_type: response.data.data.mime_type,
            file_size: response.data.data.file_size
        });

        return { path: imageUrl, signedUrl: signedUrl };

    } catch (error) {
        console.error("❌ Error uploading to backend:", error);
        console.error("Respuesta del servidor:", error.response?.data);
        throw error;
    }
};

const deleteFromBackend = async (url) => {
    try {
        if (!url || !props.localNoteForm.id) {
            console.log('⚠️ No se puede eliminar: falta URL o document_id');
            return;
        }

        console.log('🗑️ Eliminando imagen del backend:', url);
        console.log(`   Usando: DELETE /patient-documents/${props.localNoteForm.id}/image`);

        // Usar el nuevo endpoint específico para eliminar imagen
        const response = await api.delete(`/patient-documents/${props.localNoteForm.id}/image`, {
            data: {
                image_url: url
            }
        });

        if (response.status === 200 || response.status === 204) {
            console.log('✅ Imagen eliminada exitosamente del backend');
        }
    } catch (error) {
        console.error("❌ Error deleting file from backend:", error);
        console.error("   Respuesta:", error.response?.data);
        // No lanzar el error para que la eliminación local continúe
    }
};

const saveImagesToServer = async () => {
    try {
        // Guardar solo los paths relativos en BD (no las signed URLs)
        const validImages = images.value
            .map((url, index) => ({ index, url }))
            .filter(item => item.url !== null);

        const imagesData = {
            images: validImages,
            imagesPerRow: imagesPerRow.value
        };

        console.log('💾 Guardando imágenes usando endpoint específico:', `PUT /patient-documents/${props.localNoteForm.id}/images`);

        // Usar el nuevo endpoint específico para imágenes
        const response = await api.put(`/patient-documents/${props.localNoteForm.id}/images`, {
            images_url: JSON.stringify(imagesData) // Solo paths relativos
        });

        if (response.status !== 200) {
            throw new Error('Error saving images to server');
        }

        console.log('✅ Imágenes guardadas exitosamente');
        emitUpdateImagesUrl();
    } catch (error) {
        console.error("Error saving images to server:", error);
    }
};

const emitUpdateImagesUrl = () => {
    const validImages = images.value
        .map((url, index) => ({ index, url }))
        .filter(item => item.url !== null);

    const imagesData = {
        images: validImages,
        imagesPerRow: imagesPerRow.value
    };

    emit('update:imagesUrl', JSON.stringify(imagesData));
};

const loadImagesFromProps = async () => {
    try {

        if (props.localNoteForm.images_url) {
            const parsedData = JSON.parse(props.localNoteForm.images_url);
            const parsedImages = parsedData.images;
            imagesPerRow.value = parsedData.imagesPerRow || 2;

            // Resetear arrays
            images.value = new Array(8).fill(null);
            signedImages.value = new Array(8).fill(null);

            // Cargar paths relativos
            parsedImages.forEach(item => {
                if (item.index >= 0 && item.index < images.value.length) {
                    images.value[item.index] = item.url; // Path relativo
                }
            });

            // Si el backend envía signed_images, usarlas para mostrar
            if (props.localNoteForm.signed_images && Array.isArray(props.localNoteForm.signed_images)) {

                props.localNoteForm.signed_images.forEach(signedImg => {
                    // Buscar el índice correspondiente al path original
                    const index = parsedImages.findIndex(img => img.url === signedImg.original_url);
                    if (index >= 0 && index < signedImages.value.length) {
                        signedImages.value[parsedImages[index].index] = signedImg.signed_url;
                    }
                });
            } else {
                console.log('⚠️ No se recibieron signed_images, solicitando al backend...');
                // Si no hay signed_images, solicitarlas al backend
                await fetchSignedUrls();
            }
        } else {
            images.value = new Array(8).fill(null);
            signedImages.value = new Array(8).fill(null);
        }
    } catch (error) {
        console.error("Error loading images from props:", error);
    }
};

// Función para solicitar URLs firmadas al backend
const fetchSignedUrls = async () => {
    try {
        if (!props.localNoteForm.id) {
            console.log('⚠️ No hay document_id, no se pueden obtener URLs firmadas');
            return;
        }

        console.log('📡 Solicitando URLs firmadas al backend para documento:', props.localNoteForm.id);
        console.log('📦 images_url actual:', props.localNoteForm.images_url);

        const response = await api.get(`/patient-documents/${props.localNoteForm.id}`);

        console.log('📥 Respuesta completa del backend:', response.data);
        console.log('🔍 signed_images en respuesta:', response.data?.data?.signed_images);
        console.log('🔍 images_url en respuesta:', response.data?.data?.images_url);

        if (response.data?.data?.signed_images && Array.isArray(response.data.data.signed_images)) {
            console.log('✅ URLs firmadas obtenidas:', response.data.data.signed_images.length);

            if (response.data.data.signed_images.length === 0) {
                console.warn('⚠️ Array de signed_images está vacío. Posibles causas:');
                console.warn('   1. El campo images_url está vacío en la BD');
                console.warn('   2. El backend no está procesando images_url correctamente');
                console.warn('   3. Las rutas en images_url no existen en Google Cloud');
                return;
            }

            const parsedData = JSON.parse(props.localNoteForm.images_url);
            const parsedImages = parsedData.images;

            console.log('🔍 Paths a mapear:', parsedImages);
            console.log('🔍 URLs firmadas recibidas:', response.data.data.signed_images);

            response.data.data.signed_images.forEach(signedImg => {
                const index = parsedImages.findIndex(img => img.url === signedImg.original_url);
                console.log(`   Buscando: "${signedImg.original_url}" → índice: ${index}`);
                if (index >= 0 && index < signedImages.value.length) {
                    signedImages.value[parsedImages[index].index] = signedImg.signed_url;
                    console.log(`   ✅ Asignada a posición ${parsedImages[index].index}`);
                }
            });

            console.log('🖼️ signedImages final:', signedImages.value.filter(i => i));
        } else {
            console.error('❌ El backend no retornó signed_images o no es un array');
            console.error('   Estructura recibida:', response.data?.data);
        }
    } catch (error) {
        console.error('❌ Error al obtener URLs firmadas:', error);
        console.error('   Respuesta de error:', error.response?.data);
    }
};

const triggerFileInput = (index) => {
    document.querySelectorAll('.file-input')[index].click();
};

const deleteImage = async (index) => {
    try {
        const url = images.value[index];
        if (url) {
            // Primero eliminar del backend
            await deleteFromBackend(url);

            // Luego limpiar localmente
            images.value[index] = null;
            signedImages.value[index] = null;

            // Guardar el estado actualizado
            await saveImagesToServer();
            emitUpdateImagesUrl();

            console.log('✅ Imagen eliminada completamente (backend + local)');
        }
    } catch (error) {
        console.error("Error deleting image:", error);
    }
};

watch(imagesPerRow, async (newValue, oldValue) => {
    if (newValue !== oldValue) {
        await saveImagesToServer();
    }
});

watch(() => props.localNoteForm.images_url, async (newVal, oldVal) => {
    if (newVal !== oldVal) {
        await loadImagesFromProps();
    }
});

const onDragStart = (index) => {
    dragIndex.value = index;
};

const onDrop = (index) => {
    if (dragIndex.value !== null) {
        // Intercambiar paths relativos
        const tempPath = images.value[index];
        images.value[index] = images.value[dragIndex.value];
        images.value[dragIndex.value] = tempPath;

        // Intercambiar URLs firmadas
        const tempSigned = signedImages.value[index];
        signedImages.value[index] = signedImages.value[dragIndex.value];
        signedImages.value[dragIndex.value] = tempSigned;

        dragIndex.value = null;
        saveImagesToServer();
    }
};

const onImageError = (index) => {
    console.error(`Failed to load image at index ${index}:`, signedImages.value[index]);
    images.value[index] = null;
    signedImages.value[index] = null;
};

const gridStyle = computed(() => {
    return {
        display: 'grid',
        gridTemplateColumns: `repeat(${imagesPerRow.value}, 1fr)`
    };
});

onMounted(() => {
    loadImagesFromProps();
});
</script>

<style>
.image-uploader {
    width: 100%;
    padding: 10px;
    box-sizing: border-box;
}

.options {
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.form-select {
    padding: 5px 10px;
    border-radius: 5px;
    border: 1px solid #ddd;
    font-size: 14px;
}

.grid {
    display: grid;
    gap: 15px;
    width: 100%;
    max-width: 100%;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
}

.image-box {
    border: 2px dashed #ccc;
    background-color: #f9f9f9;
    text-align: center;
    position: relative;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s, box-shadow 0.3s;
    max-width: 100%;
    cursor: pointer;
}

.image-box:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.image-box img {
    max-width: 100%;
    max-height: 200px;
    object-fit: contain;
    margin-top: 10px;
    border-radius: 5px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.file-input {
    display: none;
}

.buttons {
    display: flex;
    justify-content: center;
    gap: 10px;
    width: 100%;
    margin-top: auto;
    padding: 10px 0px !important;
}

.file-button,
.delete-button {
    background-color: #007bff;
    color: white;
    border: none;
    padding: 5px 16px !important;
    font-size: 12px !important;
    font-weight: bold;
    cursor: pointer;
    border-radius: 5px;
    transition: background-color 0.3s ease, transform 0.2s;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.file-button:hover {
    background-color: #0056b3;
    transform: translateY(-2px);
}

.delete-button {
    background-color: #dc3545;
}

.delete-button:hover {
    background-color: #c82333;
    transform: translateY(-2px);
}
</style>
